import { useMemo, useState } from 'react';
import { useEditor } from '../state/store';
import type { SectionGroup } from '../api/types';

interface Props {
  group: SectionGroup;
  onClose(): void;
}

/**
 * Shopify-style "Add section" modal: white surface, rounded thumbnails,
 * category sidebar-less view with search on top and grouped results.
 */
export function AddSectionDialog({ group, onClose }: Props) {
  const available = useEditor((s) => s.metadata?.config.availableSections || []);
  const schemas = useEditor((s) => s.metadata?.theme.schemas || {});
  const addSection = useEditor((s) => s.addSection);
  const select = useEditor((s) => s.select);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  /*
    Sections que NÃO devem aparecer no modal "Adicionar seção":

    - `search-results`: só renderiza na página /search, não faz sentido
      adicionar em outras páginas.
    - `product-content`: era um bloco interno do main-product, não
      section standalone — adicionada por engano corrompe o template.
    - `collection-banner`: DEPRECATED, substituído pelo `main-collection`.
      Pode ainda aparecer via cache S3/Mongo mas não renderiza nada.
    - `email-signup-banner`: legado, substituído por `newsletter`.

    `social-media` foi DESBLOQUEADO (era bloqueado pensando que só
    funcionava como bloco — incorreto, é section standalone com
    presets de Reels/Stories/Popup que o lojista quer adicionar
    livremente). Bug reportado: "FUI ADICIONAR UMA SECAO E CADE
    OUTRAS SECOES? COMO SOCIAL MEDIA?"
  */
  const BLOCKED_TYPES = new Set([
    'search-results',
    'product-content',
    'collection-banner',
    'email-signup-banner',
  ]);

  /*
    Sections que SÓ podem ir em grupos específicos. `cart-drawer-config`
    é a section de configuração do drawer — só faz sentido dentro do
    `cart-drawer-group`. Se aparecer no template/header/footer, vira
    órfã (declara CSS vars que ninguém consome).
  */
  const GROUP_RESTRICTED: Record<string, SectionGroup[]> = {
    'cart-drawer-config': ['cart-drawer-group'],
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = available.filter((s) => {
      if (s.type.startsWith('main-') || s.type.endsWith('-group')) return false;
      if (BLOCKED_TYPES.has(s.type)) return false;
      const allowedGroups = GROUP_RESTRICTED[s.type];
      if (allowedGroups && !allowedGroups.includes(group)) return false;
      if (!q) return true;
      return (
        s.type.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q)
      );
    });
    list.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
    return list;
  }, [available, filter, group]);

  const groups = useMemo(() => {
    const m = new Map<string, typeof filtered>();
    for (const s of filtered) {
      if (!m.has(s.category)) m.set(s.category, []);
      m.get(s.category)!.push(s);
    }
    return Array.from(m.entries());
  }, [filtered]);

  async function handleAdd(type: string) {
    setBusy(type);
    try {
      const sid = await addSection(group, type);
      select({ kind: 'section', group, sectionId: sid });
      onClose();
    } catch (err) {
      console.error(err);
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="lc-fade-in flex h-[620px] w-[720px] max-w-[94vw] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <div>
            <h2 className="text-[15px] font-semibold text-text">Adicionar seção</h2>
            <p className="mt-0.5 text-[12px] text-text-subdued">
              Escolha um tipo de seção para adicionar ao seu tema.
            </p>
          </div>
          <button
            className="lc-btn-ghost flex h-8 w-8 items-center justify-center"
            onClick={onClose}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="border-b border-border px-5 py-3">
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-icon-subdued"
              width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              autoFocus
              className="lc-input pl-9"
              placeholder="Buscar seção…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>

        <div className="lc-scroll flex-1 overflow-y-auto px-5 py-4">
          {groups.map(([cat, items]) => (
            <div key={cat} className="mb-5">
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-subdued">
                {translateCategory(cat)}
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {items.map((s) => (
                  <button
                    key={s.type}
                    disabled={busy !== null}
                    className="group flex flex-col gap-2 rounded-lg border border-border bg-surface p-2.5 text-left transition-all hover:-translate-y-0.5 hover:border-interactive hover:shadow-card disabled:opacity-50"
                    onClick={() => handleAdd(s.type)}
                  >
                    <div className="flex h-16 items-center justify-center rounded-md bg-surface-subdued">
                      <SectionThumb type={s.type} />
                    </div>
                    <div>
                      <div className="text-[13px] font-medium text-text">
                        {schemas[s.type]?.name || s.name}
                        {busy === s.type && (
                          <span className="lc-pulse ml-1.5 text-[11px] text-text-subdued">adicionando…</span>
                        )}
                      </div>
                      <div className="text-[11px] text-text-subdued">{s.type}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {groups.length === 0 && (
            <p className="mt-10 text-center text-[13px] text-text-subdued">
              Nenhuma seção encontrada.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

//
// Traduz as categorias que vêm em inglês do schema (metadata do engine)
// pros rótulos que o lojista vê no modal. O schema original é herdado
// da base Shopify — mantém inglês no metadata pra compat, traduz só na
// UI. Categorias não mapeadas passam direto (fail open).
//
const CATEGORY_TRANSLATIONS: Record<string, string> = {
  COLLECTIONS: 'COLEÇÕES',
  CONTENT: 'CONTEÚDO',
  FOOTER: 'RODAPÉ',
  FORMS: 'FORMULÁRIOS',
  HEADER: 'CABEÇALHO',
  OTHER: 'OUTROS',
  PRODUCTS: 'PRODUTOS',
  'SOCIAL PROOF': 'PROVAS SOCIAIS',
  STORE: 'LOJA',
};

function translateCategory(cat: string): string {
  const upper = cat.toUpperCase();
  return CATEGORY_TRANSLATIONS[upper] || cat;
}

function SectionThumb({ type }: { type: string }) {
  const t = type.toLowerCase();
  const cls = 'h-7 w-7 text-icon-subdued group-hover:text-interactive';
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' } as any;
  if (t.includes('slideshow'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="8" cy="17" r="1" /><circle cx="12" cy="17" r="1" /><circle cx="16" cy="17" r="1" /></svg>);
  if (t.includes('header'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M3 14h10M3 18h14" /></svg>);
  if (t.includes('footer'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="3" y="16" width="18" height="4" rx="1" /><path d="M3 6h10M3 10h14" /></svg>);
  if (t.includes('product'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><path d="M20 7 12 3 4 7m16 0v10l-8 4m8-14-8 4m0 0L4 7m8 4v10L4 17V7" /></svg>);
  if (t.includes('collection') || t.includes('featured'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>);
  if (t.includes('image') || t.includes('gallery') || t.includes('banner'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>);
  if (t.includes('text'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><path d="M4 7V5h16v2M9 5v14M15 19h-6" /></svg>);
  if (t.includes('newsletter'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>);
  if (t.includes('testimonial') || t.includes('review'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>);
  if (t.includes('contact'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 7h8M8 11h8M8 15h5" /></svg>);
  if (t.includes('faq'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 1 1 5.8 1c0 2-3 3-3 3M12 17h.01" /></svg>);
  if (t.includes('video'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><polygon points="5 3 19 12 5 21 5 3" /></svg>);
  if (t.includes('logo'))
    return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /></svg>);
  return (<svg viewBox="0 0 24 24" className={cls} {...stroke}><rect x="3" y="3" width="18" height="18" rx="2" /></svg>);
}
